import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CardMenu from "components/card/CardMenu";
import Card from "components/card";
import ActiveStatusBadge from "components/ui/ActiveStatusBadge";
import DataTableRowActions from "components/list/DataTableRowActions";
import ActiveStatusSegment from "components/ui/ActiveStatusSegment";
import DataTablePagination from "components/ui/DataTablePagination";
import DataTableScrollContainer from "components/ui/DataTableScrollContainer";
import { DataTableEmptyState, DataTableLoadingState } from "components/ui/DataTableStates";
import SearchableFormSelect from "components/ui/SearchableFormSelect";
import ToggleSwitch from "components/ui/ToggleSwitch";
import {
  DATA_TABLE_BODY_ROW,
  DATA_TABLE_HEADER_ROW,
  DATA_TABLE_TABLE_CLASS,
  dataTableBodyCellClass,
  dataTableHeaderCellClass,
} from "lib/dataTableClasses";
import { adminService } from "services/adminService";
import { academicService } from "services/academicService";
import { r2Service } from "services/r2Service";
import { toastError } from "lib/toast";
import { formatLocaleNumber } from "lib/localeFormat";
import { useAppLanguage } from "hooks/useAppLanguage";
import {
  buildStudentPayload,
  flattenStudentFromApi,
} from "lib/studentForm";
import StudentCoursesModal from "views/admin/students/components/StudentCoursesModal";
import StudentWalletModal from "views/admin/students/components/StudentWalletModal";
import StudentDevicesModal from "views/admin/students/components/StudentDevicesModal";
import UserAvatar from "components/ui/UserAvatar";
import { resolveStoredUploadPreview } from "lib/storedUploadImage";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

type Student = {
  id: number;
  name: string;
  phone: string;
  courses_count?: number;
  image: string;
  image_url?: string | null;
  auth_provider?: string | null;
  is_active: boolean;
  phone_verified_at: string | null;
  wallet_balance?: number | null;
  wallet_currency?: string | null;
  created_at: string;
};

const columnHelper = createColumnHelper<Student>();

function StudentsTable() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState<Student[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);
  const [formOpen, setFormOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [image, setImage] = React.useState("");
  const [isActive, setIsActive] = React.useState(true);
  const [stageId, setStageId] = React.useState(0);
  const [parentPhone, setParentPhone] = React.useState("");
  const [gender, setGender] = React.useState("male");
  const [dateOfBirth, setDateOfBirth] = React.useState("");
  const [gradeId, setGradeId] = React.useState(0);
  const [stagesMeta, setStagesMeta] = React.useState<{ id: number; name: string; slug: string }[]>(
    []
  );
  const [gradesRaw, setGradesRaw] = React.useState<
    { id: number; name: string; stage_id: number }[]
  >([]);
  const [deleteTargetId, setDeleteTargetId] = React.useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  /** Search term applied to the API (updated on Apply, not on every keystroke). */
  const [appliedSearch, setAppliedSearch] = useState("");
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState<"" | "1" | "0">("");
  const [sortBy] = useState("id");
  const [sortDirection] = useState<"asc" | "desc">("desc");
  const [statusToggleId, setStatusToggleId] = useState<number | null>(null);
  const [sliceMeta, setSliceMeta] = useState<{
    from: number | null;
    to: number | null;
    per_page: number;
  } | null>(null);
  /** Bumps on Apply so list refreshes even when search/page unchanged. */
  const [reloadTick, setReloadTick] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [coursesStudent, setCoursesStudent] = useState<Student | null>(null);
  const [walletStudent, setWalletStudent] = useState<Student | null>(null);
  const [devicesStudent, setDevicesStudent] = useState<Student | null>(null);

  const loadStudents = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          page: targetPage,
          limit,
          term: appliedSearch.trim() || undefined,
          sort_by: sortBy,
          sort_direction: sortDirection,
        };
        if (statusFilter) params["filter[is_active]"] = statusFilter;
        const result = await adminService.getStudents(params);
        const meta = result?.meta;
        const rows = result?.data || [];
        const totalCount = Number(meta?.total) || 0;
        const lp = Math.max(1, Number(meta?.last_page) || 1);

        setData(rows);
        setTotal(totalCount);
        setLastPage(lp);
        setSliceMeta({
          from: meta?.from ?? null,
          to: meta?.to ?? null,
          per_page: Number(meta?.per_page) || limit,
        });
        if (targetPage > lp) {
          setPage(lp);
        }
      } catch (error) {
        console.error("Failed to fetch students:", error);
      } finally {
        setLoading(false);
      }
    },
    [appliedSearch, limit, sortBy, sortDirection, statusFilter]
  );

  useEffect(() => {
    void loadStudents(page);
  }, [page, appliedSearch, statusFilter, reloadTick, loadStudents]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  useEffect(() => {
    const load = async () => {
      try {
        const [stages, grades] = await Promise.all([
          academicService.listStages({ per_page: 500 }),
          academicService.listGrades({ per_page: 500 }),
        ]);
        const sData = stages?.data || [];
        const gData = grades?.data || [];
        setStagesMeta(sData.map((s: any) => ({ id: s.id, name: s.name, slug: s.slug || "" })));
        setGradesRaw(
          gData.map((g: any) => ({
            id: g.id,
            name: g.name,
            stage_id: Number(g.stage_id),
          }))
        );
      } catch {
        setStagesMeta([]);
        setGradesRaw([]);
      }
    };
    load();
  }, []);

  const defaultStageId = stagesMeta[0]?.id ?? 0;

  const gradeOptions = gradesRaw.filter((g) => g.stage_id === stageId);

  React.useEffect(() => {
    setGradeId(0);
  }, [stageId]);

  const handleBlockUnblock = async (id: number, isBlocked: boolean) => {
    try {
      if (isBlocked) {
        await adminService.unblockStudent(id);
      } else {
        await adminService.blockStudent(id);
      }
      setData((prev) =>
        prev.map((s) => (s.id === id ? { ...s, is_active: !isBlocked } : s))
      );
    } catch (error) {
      console.error("Failed to update student:", error);
    }
  };

  const resetForm = () => {
    setName("");
    setPhone("");
    setImage("");
    setIsActive(true);
    setStageId(defaultStageId);
    setParentPhone("");
    setGender("male");
    setDateOfBirth("");
    setGradeId(0);
  };

  const openCreate = () => {
    setEditingId(null);
    resetForm();
    setFormOpen(true);
  };

  const openEdit = async (student: Student) => {
    setEditingId(student.id);
    try {
      const entity = await adminService.getStudent(student.id);
      const f = flattenStudentFromApi(entity as Record<string, unknown>);
      setName(String(f.name ?? ""));
      setPhone(String(f.phone ?? ""));
      setImage(String(f.image ?? ""));
      setIsActive(Boolean(f.is_active));
      setStageId(Number(f.stage_id) || defaultStageId);
      setParentPhone(String(f.parent_phone ?? ""));
      setGender(String(f.gender ?? "male"));
      setDateOfBirth(String(f.date_of_birth ?? ""));
      setGradeId(Number(f.grade_id) || 0);
    } catch {
      setName(student.name || "");
      setPhone(student.phone || "");
    }
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const raw = {
        name,
        phone,
        image: image || "",
        is_active: isActive,
        stage_id: stageId,
        parent_phone: parentPhone,
        gender,
        date_of_birth: dateOfBirth,
        grade_id: gradeId,
      };
      const payload = buildStudentPayload(raw);

      if (editingId) {
        await adminService.updateStudent(editingId, payload);
      } else {
        await adminService.createStudent(payload);
      }

      setFormOpen(false);
      setEditingId(null);
      resetForm();

      await loadStudents(page);
    } catch (error) {
      console.error("Failed to submit student:", error);
      toastError(isArabic ? "تعذر حفظ الطالب، راجع البيانات." : "Could not save student. Check the form.");
    }
  };

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const result = await r2Service.uploadWithPresign(file, "avatars");
      setImage(result.url);
    } catch (error) {
      console.error("Failed to upload student image:", error);
      toastError(isArabic ? "تعذر رفع صورة الطالب." : "Failed to upload student image.");
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminService.deleteStudent(id);
      await loadStudents(page);
    } catch (error) {
      console.error("Failed to delete student:", error);
      toastError(isArabic ? "تعذر حذف الطالب." : "Could not delete student.");
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "الاسم" : "NAME"}
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center gap-2">
          <UserAvatar
            src={resolveStoredUploadPreview(
              info.row.original.image,
              info.row.original.image_url
            )}
            alt={info.getValue()}
            className="h-8 w-8 rounded-full object-cover"
          />
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "الهاتف" : "PHONE"}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm text-gray-600 dark:text-white">
          {info.getValue() || "—"}
        </p>
      ),
    }),
    columnHelper.accessor("courses_count", {
      id: "courses_count",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "عدد الكورسات" : "COURSES"}
        </p>
      ),
      cell: (info) => {
        const count = Number(info.getValue() ?? 0);
        return (
          <span className={`inline-flex min-w-8 justify-center rounded-full px-2.5 py-1 text-xs font-bold ${count > 0 ? "bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-200" : "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300"}`}>
            {count}
          </span>
        );
      },
    }),
    columnHelper.accessor((row) => row.wallet_balance ?? 0, {
      id: "wallet_balance",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "المحفظة" : "WALLET"}
        </p>
      ),
      cell: (info) => {
        const balance = Number(info.getValue() ?? 0);
        const currency = info.row.original.wallet_currency || "EGP";
        return (
          <p className="text-sm font-semibold text-navy-700 dark:text-white">
            {formatLocaleNumber(balance, isArabic, { maximumFractionDigits: 2 })}{" "}
            <span className="text-xs font-normal text-gray-500">{currency}</span>
          </p>
        );
      },
    }),
    columnHelper.accessor("auth_provider", {
      id: "auth_provider",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "طريقة التسجيل" : "SIGN-UP"}
        </p>
      ),
      cell: (info) => {
        const provider = info.getValue();
        const label =
          provider === "google"
            ? "Google"
            : provider === "apple"
              ? "Apple"
              : isArabic
                ? "هاتف"
                : "Phone";
        const tone =
          provider === "google"
            ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200"
            : provider === "apple"
              ? "bg-gray-200 text-gray-800 dark:bg-white/10 dark:text-white"
              : "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200";
        return (
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>
            {label}
          </span>
        );
      },
    }),
    columnHelper.accessor("is_active", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "الحالة" : "STATUS"}
        </p>
      ),
      cell: (info) => (
        <ActiveStatusBadge active={Boolean(info.getValue())} isArabic={isArabic} />
      ),
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "تاريخ الانضمام" : "JOINED"}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm text-gray-600 dark:text-white">
          {new Date(info.getValue()).toLocaleDateString()}
        </p>
      ),
    }),
    columnHelper.accessor("id", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "الإجراءات" : "ACTIONS"}
        </p>
      ),
      cell: (info) => (
        <DataTableRowActions
          isArabic={isArabic}
          actions={[
            {
              id: "wallet",
              label: isArabic ? "المحفظة" : "Wallet",
              variant: "primary",
              onClick: () => setWalletStudent(info.row.original),
            },
            {
              id: "devices",
              label: isArabic ? "الأجهزة" : "Devices",
              variant: "primary",
              onClick: () => setDevicesStudent(info.row.original),
            },
            {
              id: "courses",
              label: isArabic ? "إدارة الكورسات" : "Manage courses",
              variant: "primary",
              onClick: () => setCoursesStudent(info.row.original),
            },
            {
              id: "edit",
              label: isArabic ? "تعديل" : "Edit",
              variant: "warning",
              onClick: () => openEdit(info.row.original),
            },
            {
              id: "delete",
              label: isArabic ? "حذف" : "Delete",
              variant: "danger",
              onClick: () => setDeleteTargetId(info.getValue()),
            },
            {
              id: "block",
              label: info.row.original.is_active
                ? isArabic
                  ? "حظر"
                  : "Block"
                : isArabic
                  ? "فك الحظر"
                  : "Unblock",
              variant: info.row.original.is_active ? "danger" : "success",
              disabled: statusToggleId === info.row.original.id,
              onClick: () => {
                void (async () => {
                  const id = info.row.original.id;
                  setStatusToggleId(id);
                  try {
                    await handleBlockUnblock(id, !info.row.original.is_active);
                  } finally {
                    setStatusToggleId(null);
                  }
                })();
              },
            },
          ]}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const perPage = sliceMeta?.per_page ?? limit;
  const fromItem =
    total === 0 ? 0 : sliceMeta?.from ?? (page - 1) * perPage + 1;
  const toItem =
    total === 0 ? 0 : sliceMeta?.to ?? Math.min(page * perPage, total);
  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6"}>
      <header
        className={`relative flex items-center justify-between pt-4 ${
          isArabic ? "flex-row-reverse" : ""
        }`}
      >
        <div className={isArabic ? "text-right" : "text-left"}>
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "الطلاب" : "Students"}
          </div>
          <p className="text-sm text-gray-500">
            {isArabic ? `إجمالي الطلاب: ${total}` : `Total students: ${total}`}
          </p>
        </div>
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isArabic ? "بحث..." : "Search..."}
            className="w-36 rounded-lg border border-gray-200 px-2 py-1 text-xs dark:bg-navy-900 dark:text-white"
          />
          <ActiveStatusSegment
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            isArabic={isArabic}
          />
          <button
            type="button"
            onClick={() => {
              setAppliedSearch(searchTerm.trim());
              setPage(1);
              setReloadTick((n) => n + 1);
            }}
            className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white"
          >
            {isArabic ? "تطبيق" : "Apply"}
          </button>
          <button
            onClick={openCreate}
            className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white"
          >
            {isArabic ? "إضافة طالب" : "Add Student"}
          </button>
          <CardMenu />
        </div>
      </header>

      <DataTableScrollContainer className="mt-8">
        {loading ? (
          <DataTableLoadingState
            message={isArabic ? "جارٍ تحميل الطلاب..." : "Loading students..."}
          />
        ) : data.length === 0 ? (
          <DataTableEmptyState
            title={isArabic ? "لا يوجد طلاب لعرضهم" : "No students to display"}
          />
        ) : (
          <table
            className={`${DATA_TABLE_TABLE_CLASS} min-w-[920px]`}
            dir={isArabic ? "rtl" : "ltr"}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={DATA_TABLE_HEADER_ROW}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className={dataTableHeaderCellClass(isArabic, true)}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={DATA_TABLE_BODY_ROW}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={dataTableBodyCellClass(isArabic)}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableScrollContainer>

      <DataTablePagination
        isArabic={isArabic}
        page={page}
        totalPages={lastPage}
        fromItem={fromItem}
        toItem={toItem}
        totalItems={total}
        onPageChange={setPage}
        className="mt-4"
      />
      {formOpen &&
        createPortal(
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-[2px]"
          onClick={() => setFormOpen(false)}
        >
          <div
            className="my-auto flex w-full max-h-[90vh] max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 pb-3 pt-5 dark:border-white/10">
              <div>
                <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                  {editingId
                    ? isArabic
                      ? "تعديل طالب"
                      : "Edit Student"
                    : isArabic
                    ? "إضافة طالب جديد"
                    : "Add New Student"}
                </h4>
                <p className="text-xs text-gray-500">
                  {editingId
                    ? isArabic
                      ? `رقم السجل: #${editingId}`
                      : `Record ID: #${editingId}`
                    : isArabic
                    ? "أدخل البيانات المطلوبة ثم احفظ."
                    : "Fill required fields then save."}
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
                onClick={() => setFormOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={isArabic ? "الاسم" : "Name"}
                  className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={isArabic ? "رقم الهاتف" : "Phone"}
                  className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                />
                <div className="md:col-span-2 rounded-xl border border-gray-200 p-3 dark:border-white/10">
                  <p className="mb-2 text-xs font-semibold text-gray-500">
                    {isArabic ? "صورة الطالب" : "Student image"}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <UserAvatar
                      src={image}
                      alt={name || (isArabic ? "صورة الطالب" : "Student image")}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                    <label className="cursor-pointer rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600">
                      {uploadingImage
                        ? isArabic
                          ? "جارٍ الرفع..."
                          : "Uploading..."
                        : isArabic
                        ? "اختيار صورة"
                        : "Choose image"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingImage}
                        onChange={handleImageFileChange}
                      />
                    </label>
                    {image ? (
                      <button
                        type="button"
                        onClick={() => setImage("")}
                        className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
                      >
                        {isArabic ? "إزالة الصورة" : "Remove image"}
                      </button>
                    ) : null}
                  </div>
                  <input
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                    placeholder={isArabic ? "رابط الصورة (اختياري)" : "Image URL (optional)"}
                    className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                  />
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-white/10">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">
                    {isArabic ? "نشط" : "Active"}
                  </span>
                  <ToggleSwitch
                    checked={isActive}
                    onChange={setIsActive}
                    aria-label={isArabic ? "نشط" : "Active"}
                  />
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold text-gray-500">
                    {isArabic ? "المرحلة" : "Stage"}
                  </p>
                  <SearchableFormSelect
                    value={stageId || 0}
                    onChange={(next) => setStageId(Number(next))}
                    options={stagesMeta.map((s) => ({ value: s.id, label: s.name }))}
                    isArabic={isArabic}
                    numeric
                    emptyNumber={0}
                    placeholder={isArabic ? "اختر المرحلة" : "Select stage"}
                  />
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold text-gray-500">
                    {isArabic ? "النوع" : "Gender"}
                  </p>
                  <SearchableFormSelect
                    value={gender}
                    onChange={(next) => setGender(String(next))}
                    options={[
                      { value: "male", label: isArabic ? "ذكر" : "Male" },
                      { value: "female", label: isArabic ? "أنثى" : "Female" },
                    ]}
                    isArabic={isArabic}
                    allowEmpty={false}
                    placeholder={isArabic ? "النوع" : "Gender"}
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="mb-1 text-xs font-semibold text-gray-500">
                    {isArabic ? "تاريخ الميلاد" : "Date of birth"}
                  </p>
                  <div className="ef-date-field">
                    <input
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      className="ef-input ef-date-input w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-shadow focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-900 dark:text-white"
                    />
                  </div>
                </div>
                <input
                  value={parentPhone}
                  onChange={(e) => setParentPhone(e.target.value)}
                  placeholder={isArabic ? "هاتف ولي الأمر" : "Parent phone"}
                  className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 dark:bg-navy-900 dark:text-white"
                />
                <div className="md:col-span-2">
                  <p className="mb-1 text-xs font-semibold text-gray-500">
                    {isArabic ? "الصف" : "Grade"}
                  </p>
                  <SearchableFormSelect
                    value={gradeId === undefined || gradeId === null ? 0 : Number(gradeId)}
                    onChange={(next) => setGradeId(Number(next))}
                    options={gradeOptions.map((g) => ({ value: g.id, label: g.name }))}
                    isArabic={isArabic}
                    numeric
                    emptyNumber={0}
                    placeholder={isArabic ? "اختر الصف" : "Select grade"}
                  />
                </div>
              </div>
            </div>
            <div className="flex shrink-0 gap-2 border-t border-gray-200 px-5 pb-5 pt-4 dark:border-white/10">
              <button
                onClick={handleSubmit}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                {editingId
                  ? isArabic
                    ? "حفظ التعديلات"
                    : "Save Changes"
                  : isArabic
                  ? "إنشاء"
                  : "Create"}
              </button>
              <button
                onClick={() => setFormOpen(false)}
                className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
              >
                {isArabic ? "إغلاق" : "Close"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {deleteTargetId !== null &&
        createPortal(
        <div className="fixed inset-0 z-[1210] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-navy-800">
            <h4 className="text-lg font-bold text-red-600 dark:text-red-400">
              {isArabic ? "تأكيد حذف الطالب" : "Confirm student deletion"}
            </h4>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-200">
              {isArabic
                ? "سيتم حذف الطالب نهائيًا ولا يمكن التراجع عن العملية."
                : "The student will be permanently deleted and this action cannot be undone."}
            </p>
            <div className="mt-4 flex items-center gap-2 border-t border-gray-200 pt-4 dark:border-white/10">
              <button
                type="button"
                onClick={async () => {
                  await handleDelete(deleteTargetId);
                  setDeleteTargetId(null);
                }}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                {isArabic ? "نعم، حذف" : "Yes, delete"}
              </button>
              <button
                type="button"
                onClick={() => setDeleteTargetId(null)}
                className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-white"
              >
                {isArabic ? "إلغاء" : "Cancel"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <StudentCoursesModal
        open={coursesStudent !== null}
        onClose={() => setCoursesStudent(null)}
        studentId={coursesStudent?.id ?? null}
        studentName={coursesStudent?.name ?? ""}
        isArabic={isArabic}
      />
      <StudentWalletModal
        open={walletStudent !== null}
        onClose={() => setWalletStudent(null)}
        studentId={walletStudent?.id ?? null}
        studentName={walletStudent?.name ?? ""}
        isArabic={isArabic}
        onUpdated={() => void loadStudents(page)}
      />
      <StudentDevicesModal
        open={devicesStudent !== null}
        onClose={() => setDevicesStudent(null)}
        studentId={devicesStudent?.id ?? null}
        studentName={devicesStudent?.name ?? ""}
        isArabic={isArabic}
      />
    </Card>
  );
}

export default StudentsTable;
